import i18n from '../i18n/config';
// Login Page - FIXED: Multi-company support + invitation handling
import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Link,
  Alert,
  InputAdornment,
  IconButton,
  Divider,
  Container,
  useTheme,
  List,
  ListItem,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Fade,
  Stepper,
  Step,
  StepLabel,
  Chip
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Email,
  Lock,
  Login as LoginIcon,
  Business as BusinessIcon,
  ArrowBack as BackIcon,
  CheckCircle as CheckIcon
} from '@mui/icons-material';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';

// Import i18n hooks if you have them, otherwise remove these lines
import { useTranslation } from 'react-i18next';

import { useAuth } from '../hooks/useAuth';
import ArsenalLogo from '../components/common/ArsenalLogo';
import LoadingPage from '../components/common/LoadingPage';

// Validation schema
const validationSchema = Yup.object({
  email: Yup.string()
    .email(i18n.t('hardcoded.pages_loginpage.invalid_email_address'))
    .required(i18n.t('hardcoded.pages_loginpage.email_required')),
  password: Yup.string()
    .min(6, i18n.t('hardcoded.pages_loginpage.password_min_length'))
    .required(i18n.t('hardcoded.pages_loginpage.password_required')),
});

const LoginPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, loading } = useAuth();
  const { t } = useTranslation();

  // 🔧 NEW: Invitation parameters
  const redirectTo = searchParams.get('redirectTo');
  const inviteCode = searchParams.get('invite');
  const fromInvite = searchParams.get('fromInvite');
  const prefilledEmail = searchParams.get('email') || '';
  const companyCreated = searchParams.get('companyCreated') === '1';

  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Multi-company state
  const [loginStep, setLoginStep] = useState('credentials'); // 'credentials' or 'company_selection'
  const [availableCompanies, setAvailableCompanies] = useState([]);
  const [userCredentials, setUserCredentials] = useState(null);

  const formik = useFormik({
    initialValues: {
      email: prefilledEmail,
      password: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      await handleCredentialsSubmit(values);
    },
  });

  // 🔧 UPDATED: Handle credentials submission with invitation logic
  const handleCredentialsSubmit = async (values) => {
    setIsLoading(true);
    setLoginError('');

    try {
      const result = await login(values.email, values.password);

      if (result.success) {
        if (result.requiresCompanySelection && result.companies.length > 1) {
          // Multiple companies - show selection
          setAvailableCompanies(result.companies);
          setUserCredentials(values);
          setLoginStep('company_selection');
        } else {
          // Single company or already selected - handle post-login redirect
          handlePostLoginRedirect();
        }
      } else {
        setLoginError(t('hardcoded.pages_loginpage.login_failed_try_again'));
      }
    } catch (error) {
      setLoginError(t('hardcoded.pages_loginpage.unexpected_error_try_again'));
    } finally {
      setIsLoading(false);
    }
  };

  // 🔧 NEW: Post-login redirection logic
  const handlePostLoginRedirect = () => {
    if (redirectTo) {
      // If there's a specific redirect path
      let redirectUrl = redirectTo;

      // Add invite parameter if it exists
      if (inviteCode) {
        const separator = redirectTo.includes('?') ? '&' : '?';
        redirectUrl = `${redirectTo}${separator}invite=${inviteCode}`;
      }

      navigate(redirectUrl);
    } else if (inviteCode) {
      // If there's an invite code but no redirect, determine where to go
      if (fromInvite === 'register') {
        // User came from registration link, go to register
        navigate(`/register?invite=${inviteCode}`);
      } else {
        // Default to join-company for invite codes
        navigate(`/join-company?invite=${inviteCode}`);
      }
    } else {
      // Default redirect
      navigate('/dashboard', { replace: true });
    }
  };

  // 🔧 UPDATED: Handle company selection with invitation logic
  const handleCompanySelection = async (companyId) => {
    setIsLoading(true);
    setLoginError('');

    try {
      const result = await login(userCredentials.email, userCredentials.password, companyId);

      if (result.success) {
        handlePostLoginRedirect();
      } else {
        setLoginError(result.message || t('hardcoded.pages_loginpage.failed_to_access_company'));
      }
    } catch (error) {
      setLoginError(t('hardcoded.pages_loginpage.an_unexpected_error_occurred'));
    } finally {
      setIsLoading(false);
    }
  };

  // Go back to credentials step
  const handleBackToCredentials = () => {
    setLoginStep('credentials');
    setAvailableCompanies([]);
    setUserCredentials(null);
    setLoginError('');
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  // 🔧 NEW: Build register link with invite parameters
  const getRegisterLink = () => {
    if (inviteCode) {
      return `/register?invite=${inviteCode}`;
    }
    return '/register';
  };

  if (loading) {
    return <LoadingPage message={t('hardcoded.pages_loginpage.loading_arsenal')} />;
  }

  const renderCredentialsStep = () => (
    <Card
      elevation={0}
      sx={{
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: theme.shadows[12],
      }}
    >
      <CardContent sx={{ p: { xs: 3, md: 4 } }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <ArsenalLogo
            sx={{
              width: 80,
              height: 80,
              color: theme.palette.primary.main,
              mb: 2,
            }}
          />
          <Typography
            variant="h4"
            component="h1"
            fontWeight={700}
            color="text.primary"
            gutterBottom
          >
            {i18n.t('hardcoded.pages_loginpage.welcome_back')}</Typography>
          <Typography
            variant="body1"
            color="text.secondary"
          >
            {inviteCode ?
              t('hardcoded.pages_loginpage.sign_in_accept_invitation') :
              t('hardcoded.pages_loginpage.sign_in_to_your_arsenal')
            }
          </Typography>
        </Box>

        {/* Invitation context alert */}
        {inviteCode && (
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>{t('hardcoded.pages_loginpage.invitation_detected')}:</strong> {t('hardcoded.pages_loginpage.after_signing_in_redirect')}
            </Typography>
          </Alert>
        )}

        {companyCreated && (
          <Alert severity="success" sx={{ mb: 3 }}>
            <Typography variant="body2">
              {t('hardcoded.pages_loginpage.empresa_creada_correctamente_inicia_sesion')}</Typography>
          </Alert>
        )}

        {/* Error Alert */}
        {loginError && (
          <Alert
            severity="error"
            sx={{ mb: 3 }}
            onClose={() => setLoginError('')}
          >
            {loginError}
          </Alert>
        )}

        {/* Login Form */}
        <Box component="form" onSubmit={formik.handleSubmit}>
          <TextField
            fullWidth
            id="email"
            name="email"
            label={t('hardcoded.pages_loginpage.email_address')}
            type="email"
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.email && Boolean(formik.errors.email)}
            helperText={formik.touched.email && formik.errors.email}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Email color="action" />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 3 }}
          />

          <TextField
            fullWidth
            id="password"
            name={t('hardcoded.components_auth_createusermanually.password')}
            label={t('hardcoded.components_auth_createusermanually.password')}
            type={showPassword ? 'text' : 'password'}
            value={formik.values.password}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.password && Boolean(formik.errors.password)}
            helperText={formik.touched.password && formik.errors.password}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Lock color="action" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={t('hardcoded.pages_loginpage.toggle_password_visibility')}
                    onClick={handleClickShowPassword}
                    onMouseDown={handleMouseDownPassword}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />

          {/* 🆕 NEW: Forgot Password Link */}
          <Box sx={{ textAlign: 'right', mb: 3 }}>
            <Link
              component={RouterLink}
              to="/forgot-password"
              variant="body2"
              sx={{
                color: theme.palette.primary.main,
                textDecoration: 'none',
                fontSize: '0.875rem',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              {t('hardcoded.pages_loginpage.olvidaste_tu_contrasena')}</Link>
          </Box>

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={isLoading}
            startIcon={isLoading ? null : <LoginIcon />}
            sx={{
              py: 1.5,
              fontWeight: 600,
              fontSize: '1.1rem',
              textTransform: 'none',
              mb: 2,
            }}
          >
            {isLoading ? t('hardcoded.pages_loginpage.signing_in') : t('hardcoded.pages_loginpage.sign_in')}
          </Button>
        </Box>

        <Divider sx={{ my: 3 }}>
          <Typography variant="body2" color="text.secondary">
            {inviteCode ? t('hardcoded.pages_loginpage.dont_have_account') : t('hardcoded.pages_loginpage.new_to_arsenal_preventive_sensors')}
          </Typography>
        </Divider>

        {/* Register Link */}
        <Box sx={{ textAlign: 'center' }}>
          <Link
            component={RouterLink}
            to={getRegisterLink()}
            variant="body1"
            sx={{
              color: theme.palette.primary.main,
              textDecoration: 'none',
              fontWeight: 600,
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
          >
            {inviteCode ?
              t('hardcoded.pages_loginpage.create_account_invitation') :
              t('hardcoded.pages_loginpage.create_new_company_account')
            }
          </Link>
        </Box>
      </CardContent>
    </Card>
  );

  const renderCompanySelectionStep = () => (
    <Card
      elevation={0}
      sx={{
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: theme.shadows[12],
        maxWidth: 600,
        mx: 'auto'
      }}
    >
      <CardContent sx={{ p: { xs: 3, md: 4 } }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <BusinessIcon
            sx={{
              width: 60,
              height: 60,
              color: theme.palette.primary.main,
              mb: 2,
            }}
          />
          <Typography
            variant="h4"
            component="h1"
            fontWeight={700}
            color="text.primary"
            gutterBottom
          >
            {i18n.t('hardcoded.pages_loginpage.select_company')}</Typography>
          <Typography
            variant="body1"
            color="text.secondary"
          >
            {inviteCode ?
              t('hardcoded.pages_loginpage.select_company_join_invitation') :
              t('hardcoded.pages_loginpage.you_have_access_to_multiple')
            }
          </Typography>
        </Box>

        {/* 🔧 NEW: Show invitation info if present */}
        {inviteCode && (
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>{i18n.t('hardcoded.pages_loginpage.processing_invitation')}</strong> {t('hardcoded.pages_loginpage.after_selecting_company_redirect')}
            </Typography>
          </Alert>
        )}

        {/* Progress Stepper */}
        <Stepper activeStep={1} sx={{ mb: 4 }}>
          <Step completed>
            <StepLabel>{i18n.t('hardcoded.pages_loginpage.credentials')}</StepLabel>
          </Step>
          <Step>
            <StepLabel>{i18n.t('hardcoded.pages_loginpage.select_company')}</StepLabel>
          </Step>
        </Stepper>

        {/* Error Alert */}
        {loginError && (
          <Alert
            severity="error"
            sx={{ mb: 3 }}
            onClose={() => setLoginError('')}
          >
            {loginError}
          </Alert>
        )}

        {/* User Info */}
        <Box sx={{
          bgcolor: 'grey.50',
          p: 2,
          borderRadius: 2,
          mb: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}>
          <Avatar sx={{ bgcolor: 'primary.main' }}>
            {userCredentials?.email?.charAt(0)?.toUpperCase()}
          </Avatar>
          <Box>
            <Typography variant="subtitle2" fontWeight={600}>
              {t('hardcoded.pages_loginpage.signing_in_as')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {userCredentials?.email}
            </Typography>
          </Box>
        </Box>

        {/* Company List */}
        <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
          {i18n.t('hardcoded.pages_loginpage.available_companies')}{availableCompanies.length})
        </Typography>

        <List sx={{ mb: 3 }}>
          {availableCompanies.map((company) => (
            <ListItem key={company.company_id} disablePadding sx={{ mb: 1 }}>
              <ListItemButton
                onClick={() => handleCompanySelection(company.company_id)}
                disabled={isLoading}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: 'primary.main + 10',
                  },
                }}
              >
                <ListItemAvatar>
                  <Avatar sx={{ bgcolor: 'primary.main' }}>
                    {company.company_name.charAt(0)}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {company.company_name}
                      </Typography>
                      <Chip
                        label={company.role_display || company.role}
                        size="small"
                        color={
                          company.role === 'admin' ? 'error' :
                            company.role === 'manager' ? 'warning' : 'default'
                        }
                      />
                    </Box>
                  }
                  secondary={
                    <Typography variant="body2" color="text.secondary">
                      {t('hardcoded.pages_loginpage.member_since')} {new Date(company.joined_at).toLocaleDateString()}
                    </Typography>
                  }
                />
                <CheckIcon sx={{ color: 'action.active' }} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>

        {/* Navigation */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
          <Button
            startIcon={<BackIcon />}
            onClick={handleBackToCredentials}
            disabled={isLoading}
            color="inherit"
          >
{t('hardcoded.pages_loginpage.back')}
          </Button>

          <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
            {isLoading ? t('hardcoded.pages_loginpage.accessing') : t('hardcoded.pages_loginpage.select_a_company_to_continue')}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.primary.dark}25 100%)`,
        py: 3,
      }}
    >
      <Container maxWidth="sm">
        <Fade in timeout={500}>
          <Box>
            {loginStep === 'credentials' ? renderCredentialsStep() : renderCompanySelectionStep()}
          </Box>
        </Fade>

        {/* Footer */}
        {loginStep === 'credentials' && (
          <Box sx={{ textAlign: 'center', mt: 3 }}>
            <Typography variant="caption" color="text.secondary">
              {i18n.t('hardcoded.pages_loginpage.2025_arsenal_preventive_sensors_all')}</Typography>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default LoginPage;